import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Search, Plus, Pencil, Trash2, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useActiveTA } from "@/lib/academic";

export const TINGKATAN_OPTIONS = [
  "Pra Al-Miftah",
  "Al-Miftah",
  "Kelas 1 Reguler",
  "Kelas 3 Reguler",
  "Takhossus 1",
  "Takhossus 2",
];

function emptyForm() {
  return { nama: "", tingkatan: "", wali_kelas_id: "", tahun_ajaran: "" };
}

export default function KelasPage() {
  const { user } = useAuth();
  const canEdit = user.role === "admin" || user.role === "sekretaris";
  const { active: activeTA } = useActiveTA();

  const [items, setItems] = useState([]);
  const [ustadzList, setUstadzList] = useState([]);
  const [q, setQ] = useState("");
  const [tingkatanFilter, setTingkatanFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      const { data } = await api.get("/kelas", { params });
      const filtered = tingkatanFilter === "all"
        ? data.items
        : (data.items || []).filter((k) => k.tingkatan === tingkatanFilter);
      setItems(filtered);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q, tingkatanFilter]);

  const loadUstadz = useCallback(async () => {
    try {
      const { data } = await api.get("/ustadz", { params: { limit: 200 } });
      setUstadzList(data.items || []);
    } catch (_e) { /* ignore for ustadz role */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canEdit) loadUstadz(); }, [loadUstadz, canEdit]);

  const ustadzName = useMemo(() => {
    const m = {};
    ustadzList.forEach(u => { m[u.id] = u.nama; });
    return m;
  }, [ustadzList]);

  const openCreate = () => {
    setEditing(null);
    const f = emptyForm();
    if (activeTA) f.tahun_ajaran = activeTA.tahun_ajaran;
    setForm(f);
    setDialogOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      nama: it.nama || "", tingkatan: it.tingkatan || "",
      wali_kelas_id: it.wali_kelas_id || "", tahun_ajaran: it.tahun_ajaran || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.tingkatan) {
      toast.error("Tingkatan wajib dipilih");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.wali_kelas_id) payload.wali_kelas_id = null;
      if (editing) {
        await api.patch(`/kelas/${editing.id}`, payload);
        toast.success("Kelas diperbarui");
      } else {
        await api.post("/kelas", payload);
        toast.success("Kelas ditambahkan");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/kelas/${deleteTarget.id}`);
      toast.success("Kelas dihapus");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="kelas-page">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Data</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Data Kelas</h1>
        <p className="text-slate-500 text-sm">Kelola seluruh kelas dan tingkatannya.</p>
      </div>

      <Card className="p-4 md:p-5 border border-slate-200 shadow-sm bg-white">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input placeholder="Cari kelas atau tingkatan..." value={q} onChange={(e) => setQ(e.target.value)}
                   className="pl-9 h-10" data-testid="kelas-search-input" />
          </div>
          <Select value={tingkatanFilter} onValueChange={setTingkatanFilter}>
            <SelectTrigger className="w-full md:w-52 h-10" data-testid="kelas-tingkatan-filter">
              <SelectValue placeholder="Tingkatan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Tingkatan</SelectItem>
              {TINGKATAN_OPTIONS.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canEdit && (
            <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 gap-2"
                    data-testid="add-kelas-button">
              <Plus className="h-4 w-4" /> Tambah Kelas
            </Button>
          )}
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nama Kelas</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Tingkatan</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Wali Kelas</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Tahun Ajaran</TableHead>
                {canEdit && <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500 font-semibold">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">
                  <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                </TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">
                  Belum ada kelas.
                </TableCell></TableRow>
              ) : items.map((it) => (
                <TableRow key={it.id} data-testid={`kelas-row-${it.id}`} className="hover:bg-emerald-50/40">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center">
                        <BookOpen className="h-4 w-4" />
                      </div>
                      <span className="font-semibold text-slate-900">{it.nama}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{it.tingkatan}</TableCell>
                  <TableCell className="text-sm text-slate-700">{ustadzName[it.wali_kelas_id] || "-"}</TableCell>
                  <TableCell className="text-sm text-slate-700">{it.tahun_ajaran}</TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`edit-kelas-${it.id}`}>
                        <Pencil className="h-4 w-4 text-slate-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(it)} data-testid={`delete-kelas-${it.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Kelas" : "Tambah Kelas"}</DialogTitle>
            <DialogDescription>Isi informasi kelas.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>Nama Kelas *</Label>
              <Input required value={form.nama} onChange={(e) => setForm(f => ({ ...f, nama: e.target.value }))}
                     data-testid="kelas-form-nama" placeholder="Mis. Pra Al-Miftah A" />
            </div>
            <div>
              <Label>Tingkatan *</Label>
              <Select value={form.tingkatan} onValueChange={(v) => setForm(f => ({ ...f, tingkatan: v }))}>
                <SelectTrigger data-testid="kelas-form-tingkatan">
                  <SelectValue placeholder="Pilih tingkatan" />
                </SelectTrigger>
                <SelectContent>
                  {TINGKATAN_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tahun Ajaran *</Label>
              <Input required value={form.tahun_ajaran} onChange={(e) => setForm(f => ({ ...f, tahun_ajaran: e.target.value }))}
                     data-testid="kelas-form-tahun" placeholder="Mis. 2025/2026" />
            </div>
            <div>
              <Label>Wali Kelas</Label>
              <Select value={form.wali_kelas_id || "none"} onValueChange={(v) => setForm(f => ({ ...f, wali_kelas_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="kelas-form-wali"><SelectValue placeholder="Pilih wali kelas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Belum ditentukan</SelectItem>
                  {ustadzList.map(u => <SelectItem key={u.id} value={u.id}>{u.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="kelas-form-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Kelas?</AlertDialogTitle>
            <AlertDialogDescription>
              Kelas <span className="font-semibold">{deleteTarget?.nama}</span> akan dihapus permanen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="kelas-delete-confirm"
                               className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
